import type { Grant } from "@shared/permissions";
import type { SiteKind } from "@shared/sites";

// The hub's own API (worker/api.ts), from the screens. Same origin: the session cookie goes along by itself.

export type Me = { email: string; name: string; role: "owner" | "member" };
export type HubSite = { id: string; name: string; url: string; kind: SiteKind; apiBase: string; off: string[]; owner: boolean; grant: Grant };
export type HubUser = { email: string; name: string; role: "owner" | "member"; disabled: boolean; createdAt: string; createdBy: string; lastSeen: string | null; grants: Record<string, Grant> };
export type AuditEntry = { id: number; at: string; email: string; action: string; detail: string };

export class SignedOut extends Error {}

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, { method, headers: body === undefined ? {} : { "Content-Type": "application/json" }, body: body === undefined ? undefined : JSON.stringify(body) });
  if (res.status === 401) throw new SignedOut("Please sign in.");
  const data = (await res.json().catch(() => ({ ok: false, error: "The hub didn't answer. Check your connection." }))) as T & { ok?: boolean; error?: string };
  if (!res.ok || data.ok === false) throw new Error(data.error ?? `The hub answered ${res.status}.`);
  return data;
}

export const hub = {
  me: () => call<Me>("GET", "/api/me"),
  sites: async () => (await call<{ sites: HubSite[] }>("GET", "/api/sites")).sites,
  siteToken: (siteId: string) => call<{ token: string; expiresAt: number; apiBase: string }>("POST", `/api/sites/${encodeURIComponent(siteId)}/token`),
  logout: () => call<{ ok: true }>("POST", "/api/auth/logout"),
  users: async () => (await call<{ users: HubUser[] }>("GET", "/api/users")).users,
  addUser: (user: { email: string; name: string; role: "owner" | "member" }) => call("POST", "/api/users", user),
  updateUser: (email: string, changes: { name?: string; role?: "owner" | "member"; disabled?: boolean }) => call("PATCH", `/api/users/${encodeURIComponent(email)}`, changes),
  removeUser: (email: string) => call("DELETE", `/api/users/${encodeURIComponent(email)}`),
  setGrant: (email: string, siteId: string, grant: Grant) => call("PUT", `/api/users/${encodeURIComponent(email)}/grants/${encodeURIComponent(siteId)}`, grant),
  audit: async (limit = 100) => (await call<{ entries: AuditEntry[] }>("GET", `/api/audit?limit=${limit}`)).entries,
};
